import { useMutation, useQuery } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';

// Types
export interface CoinPackage {
  id: number;
  name: string;
  amount: number;
  coins: number;
  description: string;
  coinType: 'regular' | 'premium';
  isActive: boolean;
}

export interface PendingTransaction {
  id: number;
  userId: number;
  reference: string;
  amount: number;
  coins: number;
  coinType: 'regular' | 'premium';
  status: 'pending' | 'completed' | 'failed';
  email: string;
  metadata?: string;
  paymentDetails?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InitiateTransactionResponse {
  success: boolean;
  paymentUrl?: string;
  reference?: string;
  message?: string;
}

export interface VerifyTransactionResponse {
  success: boolean;
  message: string;
  transaction?: PendingTransaction;
  status?: string;
}

// Hook to fetch available coin packages
export function useCoinPackages() {
  return useQuery({
    queryKey: ['/api/currency/paystack/packages'],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', '/api/currency/paystack/packages');
        const data: CoinPackage[] = await response.json();
        return data;
      } catch (error) {
        console.error('Error fetching coin packages:', error);
        throw error;
      }
    }
  });
}

// Hook to initialize a payment transaction
export function useInitiatePayment() {
  return useMutation({
    mutationFn: async (params: { packageId: number; email: string }): Promise<InitiateTransactionResponse> => {
      try {
        const response = await apiRequest('POST', '/api/currency/paystack/initiate', params);
        const data: InitiateTransactionResponse = await response.json();
        return data;
      } catch (error) {
        console.error('Error initiating payment:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // We don't need to invalidate any queries here since the transaction is just initialized
      // Invalidation will happen when the transaction is verified
    }
  });
}

// Hook to verify a payment transaction
export function useVerifyPayment() {
  return useMutation({
    mutationFn: async (reference: string): Promise<VerifyTransactionResponse> => {
      try {
        const response = await apiRequest('GET', `/api/currency/paystack/verify?reference=${reference}`);
        const data: VerifyTransactionResponse = await response.json();
        return data;
      } catch (error) {
        console.error('Error verifying payment:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        // If verification was successful, invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
        queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
        queryClient.invalidateQueries({ queryKey: ['/api/currency/pending-transactions'] });
      }
    }
  });
}